import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from '../../utils/api';

// Async thunks
export const fetchCollections = createAsyncThunk(
  'collections/fetchCollections',
  async ({ includeProducts = false } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (includeProducts) params.append('includeProducts', 'true');

      const response = await apiFetch(`/merchants/collections?${params}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch collections');
      return data.data || [];
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch collections');
    }
  }
);

export const fetchCollection = createAsyncThunk(
  'collections/fetchCollection',
  async (collectionId, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`/merchants/collections/${collectionId}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch collection');
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch collection');
    }
  }
);

export const createCollection = createAsyncThunk(
  'collections/createCollection',
  async (collectionData, { rejectWithValue }) => {
    try {
      const response = await apiFetch('/merchants/collections', {
        method: 'POST',
        body: JSON.stringify(collectionData)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create collection');
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create collection');
    }
  }
);

export const updateCollection = createAsyncThunk(
  'collections/updateCollection',
  async ({ id, data: updateData }, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`/merchants/collections/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update collection');
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update collection');
    }
  }
);

export const deleteCollection = createAsyncThunk(
  'collections/deleteCollection',
  async (collectionId, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`/merchants/collections/${collectionId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete collection');
      return collectionId;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete collection');
    }
  }
);

export const duplicateCollection = createAsyncThunk(
  'collections/duplicateCollection',
  async (collectionId, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`/merchants/collections/${collectionId}/duplicate`, {
        method: 'POST'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to duplicate collection');
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to duplicate collection');
    }
  }
);

export const addProductsToCollection = createAsyncThunk(
  'collections/addProducts',
  async ({ collectionId, productIds }, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`/merchants/collections/${collectionId}/products`, {
        method: 'POST',
        body: JSON.stringify({ productIds })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to add products');
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add products');
    }
  }
);

export const removeProductFromCollection = createAsyncThunk(
  'collections/removeProduct',
  async ({ collectionId, productId }, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`/merchants/collections/${collectionId}/products/${productId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to remove product');
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to remove product');
    }
  }
);

export const reorderProducts = createAsyncThunk(
  'collections/reorderProducts',
  async ({ collectionId, productOrders }, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`/merchants/collections/${collectionId}/products/reorder`, {
        method: 'PUT',
        body: JSON.stringify({ productOrders })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to reorder products');
      return data.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to reorder products');
    }
  }
);

export const createDefaultCollections = createAsyncThunk(
  'collections/createDefaults',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiFetch('/merchants/collections/create-defaults', {
        method: 'POST'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create default collections');
      return data.data || [];
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create default collections');
    }
  }
);

export const regenerateShareToken = createAsyncThunk(
  'collections/regenerateShareToken',
  async (collectionId, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`/merchants/collections/${collectionId}/regenerate-share-token`, {
        method: 'POST'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to regenerate share token');
      return { collectionId, shareToken: data.data?.shareToken };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to regenerate share token');
    }
  }
);

const initialState = {
  collections: [],
  currentCollection: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  total: 0
};

const collectionsSlice = createSlice({
  name: 'collections',
  initialState,
  reducers: {
    clearCurrentCollection: (state) => {
      state.currentCollection = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch collections
      .addCase(fetchCollections.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCollections.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.collections = action.payload;
        state.error = null;
      })
      .addCase(fetchCollections.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Fetch single collection
      .addCase(fetchCollection.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchCollection.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentCollection = action.payload;
        state.error = null;
      })
      .addCase(fetchCollection.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })

      // Create collection
      .addCase(createCollection.fulfilled, (state, action) => {
        state.collections.unshift(action.payload);
        state.currentCollection = action.payload;
      })

      // Update collection
      .addCase(updateCollection.fulfilled, (state, action) => {
        const index = state.collections.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.collections[index] = action.payload;
        }
        state.currentCollection = action.payload;
      })

      // Delete collection
      .addCase(deleteCollection.fulfilled, (state, action) => {
        state.collections = state.collections.filter(c => c.id !== action.payload);
        if (state.currentCollection?.id === action.payload) {
          state.currentCollection = null;
        }
      })

      // Duplicate collection
      .addCase(duplicateCollection.fulfilled, (state, action) => {
        state.collections.unshift(action.payload);
      })

      // Add products
      .addCase(addProductsToCollection.fulfilled, (state, action) => {
        const index = state.collections.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.collections[index] = action.payload;
        }
        state.currentCollection = action.payload;
      })

      // Remove product
      .addCase(removeProductFromCollection.fulfilled, (state, action) => {
        const index = state.collections.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.collections[index] = action.payload;
        }
        state.currentCollection = action.payload;
      })

      // Reorder products
      .addCase(reorderProducts.fulfilled, (state, action) => {
        const index = state.collections.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.collections[index] = action.payload;
        }
        state.currentCollection = action.payload;
      })

      // Create defaults
      .addCase(createDefaultCollections.fulfilled, (state, action) => {
        state.collections = [...action.payload, ...state.collections];
      })

      // Regenerate share token
      .addCase(regenerateShareToken.fulfilled, (state, action) => {
        const { collectionId, shareToken } = action.payload;
        const index = state.collections.findIndex(c => c.id === collectionId);
        if (index !== -1) {
          state.collections[index].shareToken = shareToken;
        }
        if (state.currentCollection?.id === collectionId) {
          state.currentCollection.shareToken = shareToken;
        }
      });
  }
});

export const { clearCurrentCollection, clearError } = collectionsSlice.actions;

// Selectors
export const selectCollections = (state) => state.collections.collections;
export const selectCurrentCollection = (state) => state.collections.currentCollection;
export const selectCollectionsStatus = (state) => state.collections.status;
export const selectCollectionsError = (state) => state.collections.error;

export default collectionsSlice.reducer;
